import { Injectable } from '@angular/core';
import {
  AuthenticationService,
  UserPersonalData,
} from '../authentication.service';
import { ALL_FEATURES_AVAILABLE, SubscriptionPlans } from '../user/user.model';
import { BehaviorSubject, catchError, interval, map, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { JsonResponse } from '../../shared/api/backend-config';
import { ModalService } from '../../shared/services/modal.service';
import { registrationRequiredText } from '../map-redirect-modal/map-redirect-modal-text';
import { GoogleAnalyticsService } from '../google-analytics.service';
import {
  ONE_DAY_MS,
} from '../../shared/services/last-visit.service';

export type MapLimitsStatus = {
  greenRateExceeded?: boolean;
  yellowRateExceeded?: boolean;
  redRateExceeded: boolean;
};
export type FeatureType = string;

export const FEATURES_CLICKED = 'FEATURES_CLICKED';
export const FEATURES_CLICKED_DATE = 'FEATURES_CLICKED_DATE';

/**
 * Union above all user statuses.
 * Unknown means the data is still being loaded.
 * Trial means "authorized user with no subscription".
 * Note that subscription plan has priority other 'admin' so admin can check how actual plan looks like.
 */
export type UserAccessStatus = 'unknown' | 'unauthorized' | 'trial' | SubscriptionPlans | 'admin';

@Injectable({
  providedIn: 'root',
})
export class UserAccessService {
  // We need it to trigger change detection for pipes in menu toggles
  public isUserPlanUpdated: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public isRedRateExceeded = new BehaviorSubject<boolean | undefined>(
    undefined
  );


  public accessStatus: BehaviorSubject<UserAccessStatus> = new BehaviorSubject<UserAccessStatus>('unknown');

  public isPaidPlanActive: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public hasPaidPlan(): boolean {
    return this.isPaidPlanActive.value;
  }

  private $userPlan = new BehaviorSubject<SubscriptionPlans | null>(null);

  public userPlan = this.$userPlan.asObservable();

  private isAdmin = false;

  private isTrial = false;
  private createdAt: string | null = null;

  private readonly _isUnauthorized$ = new BehaviorSubject<boolean>(true);
  public readonly isAuthorized$ = this._isUnauthorized$.pipe(map(v => !v));

  get isUnauthorized(): boolean {
    return this._isUnauthorized$.value;
  }

  private set isUnauthorized(value: boolean) {
    this._isUnauthorized$.next(value);
  }

  public isFeaturesClickLimitExceeded = false;

  public isMapLoadingRestricted = false;

  constructor(
    private authService: AuthenticationService,
    private http: HttpClient,
    private modalService: ModalService,
    private gaService: GoogleAnalyticsService
  ) {
    authService.userPersonalData.subscribe((user) => this.setUserPlan(user!));
    this.isFeaturesClickLimitExceeded =
      Number(localStorage.getItem(FEATURES_CLICKED)) >
      SubscriptionPlans.unauthorizedUser.actionsLimit;

    this.updateActionsLimit();

    // Check if trial expired and actionsLimit each N sec or actionsLimit is to reset
    // 1 sec is too often: trial modals are noisy then
    interval(3_000).subscribe(() => {
      this.updateActionsLimit();
    });
  }

  /**
   * @return true if current user has sufficient access to feature/component.
   * Note: frontend access checks are for UX.
   * Backend checks actual access for all the endpoints for security.
   */
  public userHasAccess(feature: FeatureType): boolean {
    if (!this.authService.hasAccessToken()) {
      const accessibleFeatures =
        SubscriptionPlans.unauthorizedUser.accessibleFeatures;
      return (
        accessibleFeatures.includes(feature) ||
        accessibleFeatures.includes(feature.toLowerCase()) ||
        accessibleFeatures === ALL_FEATURES_AVAILABLE
      );
    }

    if (this.isTrial) {
      const accessibleFeatures =
        SubscriptionPlans.trialDescription.accessibleFeatures;
      return (
        accessibleFeatures.includes(feature) ||
        accessibleFeatures.includes(feature.toLowerCase()) ||
        accessibleFeatures === ALL_FEATURES_AVAILABLE
      );
    }

    if (this.$userPlan.value) {
      const accessibleFeatures =
        SubscriptionPlans.description[this.$userPlan.value as SubscriptionPlans]
          .accessibleFeatures;
      return (
        accessibleFeatures.includes(feature) ||
        accessibleFeatures.includes(feature.toLowerCase()) ||
        accessibleFeatures === ALL_FEATURES_AVAILABLE
      );
    }

    return false;
  }

  public setUserPlan(info: UserPersonalData | null): void {
    if (info) {
      this.isUserPlanUpdated.next(false);
      this.$userPlan.next(info?.subscription?.plan || null);
      this.isAdmin = info?.isAdmin || false;
      this.isTrial = !this.isAdmin && this.$userPlan.value === null;
      this.isUnauthorized = false;
      this.createdAt = info.createdAt || null;
      // Reset limited clicks count if user authorized
      localStorage.setItem(FEATURES_CLICKED, '0');

      this.isMapLoadingRestricted = false;

      if (this.$userPlan.value !== null) {
        this.isPaidPlanActive.next(true);
      }

      // this.updateTrialExpiredStatus(info.createdAt);

      // if (this.isTrial && this.subscriptionsService.accessibleFeaturesList.length === 0) {
      //   this.subscriptionsService.setAccessibleFeaturesId()
      // } else if (!this.isTrial) {
      //   this.subscriptionsService.setAccessibleFeaturesAll()
      // }

      this.isUserPlanUpdated.next(true);

      // Plan takes priority over admin so an admin with a subscription gets their plan features
      this.updateStatus(this.$userPlan.value ?? (this.isAdmin ? 'admin' : 'trial'));
    } else if (info === null) {
      this.isUserPlanUpdated.next(false);

      this.$userPlan.next(null);
      this.isAdmin = false;
      this.isTrial = false;
      this.isUnauthorized = true;
      this.isPaidPlanActive.next(false);
      this.isUserPlanUpdated.next(true);

      this.updateStatus('unauthorized');
    }
  }

  private updateStatus(status: UserAccessStatus): void {
    if (this.accessStatus.value !== status) {
      this.accessStatus.next(status);
    }
  }


  public getUserPlan(): SubscriptionPlans | null {
    return this.$userPlan.value;
  }

  public getAccessibleLevels(): string[] {
    if (this.isUnauthorized) {
      return SubscriptionPlans.unauthorizedUser.accessibleLevels;
    } else if (this.isTrial) {
      return SubscriptionPlans.trialDescription.accessibleLevels;
    } else {
      return this.$userPlan.value
        ? SubscriptionPlans.description[this.$userPlan.value].accessibleLevels
        : [];
    }
  }

  public getIsAdmin(): boolean {
    return this.isAdmin;
  }

  public getIsUnauthorized(): boolean {
    return this.isUnauthorized;
  }

  public getIsAuthorized(): boolean {
    return !this.isUnauthorized;
  }

  public getIsTrial(): boolean {
    return this.isTrial;
  }

  /**
   * Change subscription of given user.
   * Works only for admins which is assured by backend.
   */
  public grantSubscription(
    toWhomEmail: string,
    plan: SubscriptionPlans,
    durationDays = 1_000,
    sendEmail = false
  ): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}user/subscription/grant` +
        `?existingUserEmail=${toWhomEmail}&plan=${plan}&durationDays=${durationDays}&sendEmail=${sendEmail}`,
      {}
    );
  }

  public triggerMapLoadedEvent(): void {
    this.http
      .post(`${environment.apiUrl}user/metrics/events/map-loaded`, {})
      .subscribe();
  }

  public triggerSiteOpenedEvent(): void {
    this.http
      .post(`${environment.apiUrl}user/metrics/events/site-opened`, {})
      .subscribe();
  }

  public checkMapUnauthorizedAccessibility(): void {
    this.http
      .get<JsonResponse<MapLimitsStatus>>(
        `${environment.apiUrl}user/metrics/map-limits`
      )
      .pipe(
        catchError((err) => {
          console.warn(err);
          // In case of error emit undefined value as it has to be processed later
          return of({ error: null, object: { redRateExceeded: undefined } });
        })
      )
      .subscribe((data) => {
        this.isRedRateExceeded.next(data.object.redRateExceeded);
      });
  }

  /**
   * Opens the in-place sign-up modal (free-trial pitch). Resolves to
   * true when the user ends up authenticated.
   */
  public showSignInRequiredModal(): Promise<boolean> {
    this.gaService.logClicksReachedLimit();
    return this.modalService.requestAuth({
      headerText: registrationRequiredText.headerText,
      subHeaderText: registrationRequiredText.mainText,
    });
  }

  public checkAccessIfUnauthorized(): boolean {
    if (this.isUnauthorized) {
      if (this.isFeaturesClickLimitExceeded) {
        this.showSignInRequiredModal();
        this.isMapLoadingRestricted = true;
        return false;
      } else {
        this.handleUnauthorizedClick();
      }
    }

    this.isMapLoadingRestricted = false;
    return true;
  }

  public getIsClicksLimitExceeded(): boolean {
    return this.isFeaturesClickLimitExceeded;
  }

  private handleUnauthorizedClick(): void {
    let clicksDone = Number(localStorage.getItem(FEATURES_CLICKED)) || 0;
    clicksDone += 1;
    localStorage.setItem(FEATURES_CLICKED, String(clicksDone));

    // If limit reached mark exceeded and record reset timestamp
    if (clicksDone >= SubscriptionPlans.unauthorizedUser.actionsLimit) {
      this.isFeaturesClickLimitExceeded = true;
      localStorage.setItem(FEATURES_CLICKED_DATE, String(Date.now()));
    }
  }

  private updateActionsLimit(): void {
    const now = Date.now();
    const lastResetTs =
      Number(localStorage.getItem(FEATURES_CLICKED_DATE)) || 0;

    // If limit was exceeded and 24h passed since it was set
    if (this.isFeaturesClickLimitExceeded && now - lastResetTs > ONE_DAY_MS) {
      // reset the click counter and clear the exceeded flag
      localStorage.setItem(FEATURES_CLICKED, '0');
      localStorage.setItem(FEATURES_CLICKED_DATE, String(now));
      this.isFeaturesClickLimitExceeded = false;
      this.modalService.closeGenericModal();
    }
  }
}
